// @ts-nocheck
import fieldSizeToButtonSize from "@store/Framework/Common/Utilities/fieldSizeToButtonSize";
import FlexWrapper, {
    flexWrapperGapStyles,
    FlexWrapperPresentationProps,
} from "@store/Library/Components/Toolkit/FlexWrapper";
import { FormFieldSizeVariant } from "@store/MobiusOverrides/FormField/FormField";
/* Store Custom Code
import ProductAddToCartButton, {
    ProductAddToCartButtonProps,
    useShowProductAddToCartButton,
} from "@store/Overrides/Components/ProductAddToCartButton";
*/
/* Store Custom Code
import ProductQuantityOrdered, {
    ProductQuantityOrderedProps,
    useShowProductQuantityOrdered,
} from "@store/Overrides/Components/ProductQuantityOrdered";
*/
/* Store Custom Code
import ProductUnitOfMeasureSelect, {
    ProductUnitOfMeasureSelectProps,
    useShowProductUnitOfMeasureSelect,
} from "@store/Overrides/Components/ProductUnitOfMeasureSelect";
*/
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { ButtonSizeVariant } from "@toolkit/Framework/Types/Button";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { useState } from "react";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import useCanOnlyOrderSample from "@store/Framework/Common/Hooks/samples/useCanOnlyOrderSample";
import { useIsB2BCustomer } from "@store/Framework/Store/Data/BillTos/BillTosHooks";
import ProductAddSampleToCartButton, {
    ProductAddSampleToCartButtonProps,
    useIsProductAddSampleToCartButtonAvailable,
    useShowProductAddSampleToCartButton,
} from "@store/Library/Components/ProductAddSampleToCartButton";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import ProductAddToCartButton, {
    ProductAddToCartButtonProps,
    useShowProductAddToCartButton,
} from "@store/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered, {
    ProductQuantityOrderedProps,
} from "@store/Overrides/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect, {
    ProductUnitOfMeasureSelectProps,
    useShowProductUnitOfMeasureSelect,
} from "@store/Overrides/Components/ProductUnitOfMeasureSelect";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import { useLocation } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
/* Store Custom Code - End */

type ExtendedProps<T> = T & {
    "data-test-selector"?: string;
};

export interface AddToCartOptionsAndButtonProps extends ExpandStyles<AddToCartOptionsAndButtonStyles> {
    sizeVariant?: FormFieldSizeVariant;
    buttonSizeVariant?: ButtonSizeVariant;
    showUnitOfMeasure?: boolean;
    // Store Custom Code
    showSeparatorIfHidden?: boolean;
}

export interface AddToCartOptionsAndButtonStyles {
    flexWrapper?: FlexWrapperPresentationProps;
    unitOfMeasure?: ExtendedProps<ProductUnitOfMeasureSelectProps>;
    quantityOrdered?: ExtendedProps<ProductQuantityOrderedProps>;
    addToCartButton?: ExtendedProps<ProductAddToCartButtonProps>;
    // Store Custom Code
    addSampleToCartButton?: ExtendedProps<ProductAddSampleToCartButtonProps>;
    separator?: InjectableCss;
    fallbackButton?: ButtonPresentationProps;
    switchSiteModal?: TwoButtonModalStyles;
    wrapper?: InjectableCss;
    redirectButton?: ButtonPresentationProps;
}

export const addToCartOptionsAndButtonStyles: AddToCartOptionsAndButtonStyles = {
    flexWrapper: {
        gap: pxToRem(10),
    },
    unitOfMeasure: {
        extendedStyles: {
            cssOverrides: {
                formField: css`
                    flex: 1 1 50%;
                    width: auto;
                `,
            },
        },
    },
    addToCartButton: {
        extendedStyles: {
            button: {
                css: css`
                    flex: 1 0 50%; /* Store Custom Code */
                    align-self: flex-end;
                `,
            },
        },
    },
    /* Store Custom Code - Start */
    addSampleToCartButton: {
        extendedStyles: {
            button: {
                css: css`
                    flex: 1 0 50%;
                    align-self: flex-end;
                `,
            },
        },
    },
    separator: {
        css: css`
            ${spacingStyles(pxToRem(14), pxToRem(14))}
            display: block;
            border-bottom: 1px solid ${getColor("common.border")};

            .hasSeparator + && {
                display: none;
            }
        `,
    },
    fallbackButton: {
        variant: "secondary",
        mergeCss: true,
        css: css`
            text-align: center;
            width: 100%;
        `,
    },
    switchSiteModal: {
        modal: { sizeVariant: "medium" },
        cancelButton: { variant: "secondary" },
    },
    redirectButton: {
        variant: "primary",
        mergeCss: true,
        css: css`
            width: 100%;
        `,
    },
    /* Store Custom Code - End */
};

const AddToCartOptionsAndButton: FC<AddToCartOptionsAndButtonProps> = ({
    extendedStyles,
    overridedStyles,
    sizeVariant = "default", // Store Custom Code
    buttonSizeVariant: baseButtonSizeVariant,
    showSeparatorIfHidden, // Store Custom Code
    showUnitOfMeasure: baseShowUnitOfMeasure = true, // Store Custom Code
}) => {
    const [isRedirectModalOpen, setIsRedirectModalOpen] = useState(false); // Store Custom Code

    const baseStyles = useExpandProps(addToCartOptionsAndButtonStyles, extendedStyles, overridedStyles);
    const styles = useMemo(() => {
        const buttonSizeVariant = baseButtonSizeVariant || fieldSizeToButtonSize(sizeVariant);
        const itemCss = flexWrapperGapStyles(baseStyles.flexWrapper?.gap, baseStyles.flexWrapper?.verticalGap);

        return mergeProps(baseStyles, {
            unitOfMeasure: {
                extendedStyles: {
                    sizeVariant,
                    cssOverrides: {
                        formField: itemCss,
                    },
                },
            },
            quantityOrdered: {
                extendedStyles: {
                    sizeVariant,
                    cssOverrides: {
                        formField: itemCss,
                    },
                },
            },
            addToCartButton: {
                extendedStyles: {
                    button: {
                        sizeVariant: buttonSizeVariant,
                        css: itemCss,
                    },
                },
            },
            /* Store Custom Code - Start */
            addSampleToCartButton: {
                extendedStyles: {
                    button: {
                        sizeVariant: buttonSizeVariant,
                        css: itemCss,
                    },
                },
            },
            fallbackButton: {
                sizeVariant: buttonSizeVariant,
                css: itemCss,
            },
            /* Store Custom Code - End */
        } as AddToCartOptionsAndButtonStyles);
    }, [baseStyles, sizeVariant, baseButtonSizeVariant]);

    /* Store Custom Code - Start */
    const isB2BCustomer = useIsB2BCustomer();
    const isCheckoutB2CDisabled = useSettingsCollection().emserCatalogSettings.disableCheckoutB2C;

    const canShowProductAddToCartButton = useShowProductAddToCartButton(
        extendedStyles?.addToCartButton?.configurationCompleted,
        extendedStyles?.addToCartButton?.variantSelectionCompleted,
    );
    const showProductAddToCartButton = canShowProductAddToCartButton && (isCheckoutB2CDisabled ? isB2BCustomer : true);

    const canShowAddSample = useShowProductAddSampleToCartButton();
    const showProductAddSampleToCartButton = canShowAddSample && (isCheckoutB2CDisabled ? isB2BCustomer : true);

    const isProductAddSampleToCartButtonAvailable = useIsProductAddSampleToCartButtonAvailable(
        extendedStyles?.addToCartButton?.configurationCompleted,
        extendedStyles?.addToCartButton?.variantSelectionCompleted,
    );

    const { product } = React.useContext(ProductContext);
    const canOnlyOrderSample = useCanOnlyOrderSample(product);
    const showProductUnitOfMeasureSelect = useShowProductUnitOfMeasureSelect();

    const isSignedIn = useAppSelector(state => state.context.session.userName && !getCurrentUserIsGuest(state));
    const signInPage = usePageLinkByPageType(constants.pageType.signIn);
    const createB2BAccountPage = usePageLinkByPageType(constants.pageType.createB2BAccount);
    const currentLocation = useLocation();

    const redirectUrl = product?.properties?.redirectEbyEmserUrl;
    const isEbyEmserProduct = !!redirectUrl;

    const onConfirmRedirect = () => {
        if (redirectUrl) {
            window.open(redirectUrl, "_blank", "noopener,noreferrer");
        }

        setIsRedirectModalOpen(false);
    };

    const onCloseRedirectModal = () => {
        setIsRedirectModalOpen(false);
    };

    if (isEbyEmserProduct && !isB2BCustomer) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Button {...styles.redirectButton} onClick={() => setIsRedirectModalOpen(true)}>
                    {translate("Shop on E BY EMSER TILE™")}
                </Button>
                <TwoButtonModal
                    {...styles.switchSiteModal}
                    modalIsOpen={isRedirectModalOpen}
                    headlineText={translate("Proceed to E™ BY EMSER TILE")}
                    messageText={translate(
                        "You'll be redirected to E BY EMSER TILE™. Each site has its own cart and session.",
                    )}
                    cancelButtonText={translate("Stay here")}
                    submitButtonText={translate("Continue")}
                    onCancel={onCloseRedirectModal}
                    onSubmit={onConfirmRedirect}
                />
            </StyledWrapper>
        );
    }

    if (!showProductAddToCartButton && !showProductAddSampleToCartButton) {
        const returnUrl = encodeURIComponent(currentLocation.pathname + currentLocation.search);
        const signInUrl = signInPage?.url ? `${signInPage.url}?returnUrl=${returnUrl}&clientRedirect=true` : undefined;

        return null;
    }

    const showProductQuantityOrdered =
        showProductAddToCartButton || (showProductAddSampleToCartButton && isProductAddSampleToCartButtonAvailable);

    const showUnitOfMeasure = baseShowUnitOfMeasure && showProductUnitOfMeasureSelect;
    /* Store Custom Code - End */

    return (
        <FlexWrapper wrap={false} {...styles.flexWrapper}>
            {showUnitOfMeasure && <ProductUnitOfMeasureSelect {...styles.unitOfMeasure} />}
            {/* Store Custom Code - Start */}
            {showProductQuantityOrdered && (
                <ProductQuantityOrdered {...styles.quantityOrdered} isSampleQty={canOnlyOrderSample} />
            )}
            {/* Store Custom Code - End */}
            <ProductAddToCartButton {...styles.addToCartButton} />
            <ProductAddSampleToCartButton {...styles.addSampleToCartButton} /* Store Custom Code */ />
        </FlexWrapper>
    );
};

export default AddToCartOptionsAndButton;
